// ENG-7255 / ENG-7220 - host-side session-tool-bind probe gathering, extracted
// from the manager poll loop so the SAME logic can run as a one-shot from the
// `agt` CLI (operator "Probe now" in the live terminal modal).
//
// Gathers the live host signals - the agent's declared `.mcp.json`, stdio MCP
// child liveness (via `ps`), HTTP/remote MCP `tools/list` reachability, and
// whether the running session post-dates the current config - then computes the
// per-integration bound|missing|unreachable|unknown verdicts via
// `runSessionToolBindProbes`.
//
// Deliberately free of the manager's IN-MEMORY session map: the caller passes
// `sessionStartedMs` (ENG-8448 - the persisted `direct-chat-session.json`
// `startedAtMs`, the same file both callers already read for
// `sessionLoadedServerKeys`), used to compute the cold-start grace so a
// still-launching stdio broker isn't counted `missing` - so the same gather runs
// correctly in either process.

import { execFileSync as syncExecFile } from 'node:child_process';
import { readFileSync } from 'node:fs';
import { join } from 'node:path';
import type { ConnectivityTestOverride, ToolkitSourceType } from '@augmented/core/integrations';
import type { IntegrationAuthType } from '@augmented/core';
import type { McpConfig, McpServerEntry } from './stale-mcp-reaper.js';
import { findMissingMcpServers } from './mcp-presence-reaper.js';
import { parsePsRows } from './orphan-channel-mcp-reaper.js';
// ENG-9048 AC3: the Slack inbound-transport liveness signal published by
// slack-channel. Shared contract so the writer and this reader cannot drift on
// the file shape or, more importantly, on the staleness rule.
import { dirname } from 'node:path';
import {
  SLACK_SOCKET_STATE_FILENAME,
  parseSlackSocketState,
  isSlackInboundTransportDown,
} from '@augmented/core/integrations/slack-socket-state.js';
import {
  buildProbeEnv,
  buildConnectivityProbeDeps,
  deriveMcpServerKey,
} from './connectivity-probe-context.js';
import { computerUseSignatureView } from './computer-use-tunnel.js';
import { isolationMode } from './persistent-session.js';
import {
  runSessionToolBindProbes,
  type SessionToolBindRunnerResult,
} from './session-tool-bind-runner.js';

/** One installed integration as returned (enriched) by /host/agent-integrations. */
export interface SessionToolBindProbeIntegration {
  id: string;
  definition_id: string;
  scope: string;
  auth_type: string;
  source_type?: string | null;
  last_session_tool_bind_at?: string | null;
  connectivity_test?: ConnectivityTestOverride | null;
  /**
   * ENG-9114: the remaining toolkit delivery columns from
   * `/host/agent-integrations`. Declared so the runner can tell a CLI/builtin
   * integration (no `.mcp.json` server, ever) from an MCP one whose entry has
   * not been rendered yet. `source_type` alone cannot: it is a grab-bag - three
   * of these definitions are `native`, one is a remote MCP, one shells out to a
   * binary and the rest are builtins.
   */
  provider?: string | null;
  mcp_command?: string | null;
  mcp_url?: string | null;
  cli_binary?: string | null;
  cli_package?: string | null;
}

export interface GatherSessionToolBindOptions {
  /**
   * The MCP server keys the running session loaded at spawn (ENG-7263, from the
   * persisted spawn-time snapshot in direct-chat-session.json). Gates HTTP
   * `bound` per-server on membership. Empty/unknown => conservative: a pure-HTTP
   * integration stays `unknown` rather than a premature `bound`.
   */
  sessionLoadedServerKeys: ReadonlySet<string>;
  /**
   * Epoch ms the running session (re)spawned (`startedAtMs` from the persisted
   * `direct-chat-session.json`), or null when unknown. ENG-8448: within
   * `DEFAULT_COLD_START_GRACE_MS` of spawn a still-launching stdio/proxy child is
   * reported `unknown` rather than `missing`, so a forced post-restart probe
   * (ENG-7429) can't spuriously bind-quarantine it. Null ⇒ no grace (behave as
   * before - conservative on the on-demand path, where a genuinely dead broker
   * must still surface `missing` even when the spawn time is unavailable).
   */
  sessionStartedMs: number | null;
  /**
   * Due-window ms. Pass `0` to force EVERY integration due (the operator's
   * on-demand "Probe now"); the manager passes its rolling cadence (~1h).
   */
  intervalMs: number;
  /**
   * Cap on probes this run. The manager leaves this unset (runner default 25,
   * rolling); on-demand passes the agent's integration count so the whole set is
   * probed at once.
   */
  maxPerRun?: number;
}

/**
 * Run the session-tool-bind probe for ONE agent's integrations against the live
 * host. Returns the runner result (the reports to persist + counts), or `null`
 * when no verdict can be substantiated at all: an empty integration set, an
 * UNPARSEABLE `.mcp.json`, or a `ps` gather that failed while stdio servers are
 * declared.
 *
 * An ABSENT or EMPTY `.mcp.json` is NOT one of those cases (ENG-9114). It is a
 * readable answer - this agent declares no MCP servers - and runs with an empty
 * declared set, so CLI/builtin rows still report `not_applicable` and genuinely
 * MCP-backed rows are skipped rather than stamped broken.
 *
 * Never throws on host-gather failures - a failed `ps` just leaves stdio
 * liveness unknown, matching the manager's best-effort posture.
 */
/**
 * ENG-9114 — copy across only the delivery columns the payload actually carries.
 *
 * `/host/agent-integrations` enriches these from `toolkit_definitions`. An API
 * older than ENG-9114 sends none of them, and the runner must fall back to its
 * pre-ENG-9114 silence rather than read that absence as "builtin" and claim a
 * permanent `not_applicable` for an integration that is really MCP-backed —
 * which is the same class of wrong-by-default reading the ticket exists to end.
 *
 * A plain object literal cannot express that: `{ provider: i.provider }` creates
 * the key even when the value is `undefined`, so `'provider' in row` is true for
 * a payload that never mentioned it. Copying by own-property presence keeps
 * "said null" and "said nothing" apart, which is exactly the distinction the
 * runner's `hasDeliverySignal` is built on.
 *
 * Exported only so this contract can be tested directly: its whole job is the
 * difference between an absent key and a null one, which is invisible in the
 * assembled row a caller sees. (CodeRabbit, PR #4698.)
 */
export function deliveryFields(
  i: SessionToolBindProbeIntegration,
): Partial<Pick<SessionToolBindProbeIntegration,
  'source_type' | 'provider' | 'mcp_command' | 'mcp_url' | 'cli_binary' | 'cli_package'>> {
  const keys = ['source_type', 'provider', 'mcp_command', 'mcp_url', 'cli_binary', 'cli_package'] as const;
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(i, k)) out[k] = i[k];
  }
  return out;
}

export async function gatherSessionToolBindProbe(
  agent: { agent_id: string; code_name: string },
  integrations: SessionToolBindProbeIntegration[],
  projectDir: string,
  opts: GatherSessionToolBindOptions,
): Promise<SessionToolBindRunnerResult | null> {
  if (integrations.length === 0) return null;

  // The agent's declared `.mcp.json` - ground truth for which MCP servers back
  // which integration.
  //
  // ENG-9114 (CodeRabbit, PR #4698): ABSENT and UNPARSEABLE are not the same
  // thing, and collapsing them into one `return null` is what kept this
  // ticket's own target population stuck at NULL.
  //
  //   * ABSENT, or present with no servers, is a real and readable answer:
  //     this agent declares no MCP servers. That is the NORMAL state for an
  //     agent whose integrations are all CLI-delivered (github/gh, aws-cli,
  //     gcloud) or builtin - exactly the rows `not_applicable` was added for.
  //     Bailing here meant the fix never fired for the population it was
  //     written for. Continue with an EMPTY declared set instead: the runner
  //     skips MCP-backed rows as `no-declared-server` (a skip, not a write -
  //     see session-tool-bind-runner.ts) and reports the CLI/builtin ones
  //     `not_applicable`.
  //
  //   * UNPARSEABLE means we could not read the ground truth at all, so we
  //     cannot classify anything against it. Keep bailing. Asserting verdicts
  //     off a file we failed to parse would be this ticket's own defect one
  //     level up: a blank standing in for a fact nobody established.
  //
  // The empty-set path is also fail-safe under the host provisioning race
  // (ENG-9048: a shared `_mcp` mount still empty at agent boot leaves
  // `.mcp.json` absent). Genuinely MCP-backed rows are skipped rather than
  // stamped `missing`, so a host symptom cannot manufacture fleet-wide
  // integration failures.
  let mcpRaw: string | null = null;
  try {
    mcpRaw = readFileSync(join(projectDir, '.mcp.json'), 'utf-8');
  } catch {
    mcpRaw = null; // no file: a declaration of nothing, not a failure to read
  }
  let parsed: McpConfig | null = null;
  if (mcpRaw !== null) {
    try {
      parsed = JSON.parse(mcpRaw) as McpConfig | null;
    } catch {
      return null; // unparseable => no substantiated verdict is available
    }
  }
  // Normalised so `mcpServers` is always a real map: absent file, absent key and
  // a non-object value all mean the same readable thing (nothing declared), and
  // downstream readers should not each have to re-derive that.
  const declaredServers: Record<string, McpServerEntry> =
    parsed?.mcpServers && typeof parsed.mcpServers === 'object' ? parsed.mcpServers : {};
  const mcpJson: McpConfig & { mcpServers: Record<string, McpServerEntry> } = {
    ...(parsed ?? {}),
    mcpServers: declaredServers,
  };
  const declaredServerCount = Object.keys(declaredServers).length;

  // Declared stdio MCP children with NO live process this tick - computed once
  // over the whole `.mcp.json`, reusing the presence-reaper's argv/ppid matching.
  const isolated = isolationMode(agent.code_name) === 'docker';
  const hasDeclaredStdio = Object.values(mcpJson.mcpServers).some(
    (entry) => typeof (entry as { command?: unknown } | null)?.command === 'string',
  );
  let missingStdioKeys: Set<string> = new Set();
  // Nothing declared means nothing can be missing, so skip the gather entirely
  // rather than shell out (or `docker exec`) once per agent per tick to answer
  // a question with no subject. `hasDeclaredStdio` is false in that case too,
  // so the bail below would not have fired either way - this just avoids the
  // work.
  if (declaredServerCount > 0) {
    try {
      const psOutput = isolated
        ? syncExecFile('docker', ['exec', `agt-${agent.code_name}`, 'ps', '-eo', 'pid,ppid,args'], {
            encoding: 'utf-8',
            timeout: 8_000,
          })
        : syncExecFile('ps', ['-eo', 'pid,ppid,args'], { encoding: 'utf-8', timeout: 5_000 });
      missingStdioKeys = new Set(
        findMissingMcpServers({
          rows: parsePsRows(psOutput),
          codeName: agent.code_name,
          // ENG-10134: correct the computer-use entry's argv signature before
          // the same matcher the presence reaper uses looks at it. Left raw, its
          // signature resolves to the Mac's SSH USERNAME (ENG-10258), so a dead
          // child matches any sibling with a /home/<user>/ path and this probe
          // reports `bound` to the control plane for a server that is not
          // running. Deliberately the signature view and NOT the reaper's gated
          // one: while the tunnel is down the server genuinely IS unbound, and
          // reporting that is the accurate answer here.
          mcpJson: computerUseSignatureView(mcpJson) as typeof mcpJson,
          inContainer: isolated,
        }),
      );
    } catch {
      // ps gathering failed: we couldn't observe stdio child liveness this cycle.
      // An empty missing-set would make stdio-backed integrations read 'bound' on
      // no evidence, so if any declared server is stdio we bail (probe nothing this
      // run) rather than risk a false 'bound' (CodeRabbit, PR #2884). A pure-HTTP
      // set is unaffected - its liveness doesn't depend on ps.
      if (hasDeclaredStdio) return null;
    }
  }

  /**
   * ENG-10601: THE WITNESS. Did this session spawn ANY declared stdio child?
   *
   * Derived here rather than in the runner because this is the only place that
   * has both halves - the declared set from `.mcp.json` and the missing set from
   * `ps` - and because it must hold in BOTH callers. The manager has
   * `sessionBlockedOnPrompt()` available and could gate on that directly, but
   * `agt probe-tools` runs as a separate process over SSM (commands/probe-tools.ts)
   * where that module-level Map is empty: it would see "not blocked", report
   * `missing`, POST to the same endpoint and drive the same counter. An operator
   * clicking Probe now on a wedged agent would quarantine it. The witness is
   * computed from data both processes already gather, so it covers both.
   *
   * Keys with a string `command` are the ones that HAVE a child to find; a
   * URL-based entry is dialled by Claude Code directly and can never appear in
   * `ps`, so counting it would make the witness unfalsifiable. When nothing
   * declares a command there is no witness to require and this is vacuously
   * true - including the ps-failed pure-HTTP path above, which leaves
   * `missingStdioKeys` empty without asserting anything about liveness.
   */
  const declaredCommandKeys = Object.entries(mcpJson.mcpServers)
    .filter(([, entry]) => typeof (entry as { command?: unknown } | null)?.command === 'string')
    .map(([key]) => key);
  const anyStdioServerLive =
    declaredCommandKeys.length === 0 ||
    declaredCommandKeys.some((key) => !missingStdioKeys.has(key));

  // Reuse the connectivity-probe context for remote/HTTP MCP reachability: it
  // resolves the `.mcp.json` server URL + templated headers and runs a real
  // initialize -> tools/list handshake.
  // ENG-9122 AC3: carry the agent id into the probe env here too, so the bind
  // lane and the connectivity lane cannot disagree about who the probe acts for.
  const probeEnv = buildProbeEnv(projectDir, agent.agent_id);
  const probeDeps = buildConnectivityProbeDeps(projectDir, probeEnv);
  const probeHttp = async (serverKey: string): Promise<boolean | undefined> => {
    if (!probeDeps.mcpProbe) return undefined;
    const outcome = await probeDeps.mcpProbe({ serverKey, definitionId: serverKey });
    if (outcome.status === 'ok') return true;
    if (outcome.status === 'down') return false;
    return undefined; // degraded / transient_error - don't penalise
  };

  // HTTP/remote MCP servers have no child process, so reachability alone can't
  // prove THIS session loaded a (possibly newly-added) server. The runner gates
  // HTTP `bound` on the spawn-time server-key snapshot the caller supplies
  // (ENG-7263): a reachable key the session didn't load at spawn stays `unknown`.
  return runSessionToolBindProbes(
    integrations.map((i) => ({
      id: i.id,
      definition_id: i.definition_id,
      scope: i.scope as 'agent' | 'team' | 'organization',
      last_session_tool_bind_at: i.last_session_tool_bind_at ?? null,
      // ENG-9114: forwarded by PRESENCE, not by value. `hasDeliverySignal` in
      // the runner asks "did the API speak about delivery at all?", and spreading
      // these unconditionally would have made it impossible to answer: a key
      // written as `undefined` is still an own property, so an old API's omitted
      // columns would have looked identical to a current API's explicit nulls.
      // Both readings are needed and they mean opposite things - explicit null is
      // a real answer (this toolkit sets no mcp_command), absence is no answer at
      // all. Omitting the key here is what keeps them distinguishable. (CodeRabbit)
      ...deliveryFields(i),
      // Server-key hint for managed/remote-MCP kinds; the runner also tries the
      // raw + sanitised definition_id against the real `.mcp.json` keys.
      mcp_server_key: deriveMcpServerKey({
        definitionId: i.definition_id,
        sourceType: (i.source_type ?? null) as ToolkitSourceType | null,
        authType: i.auth_type as IntegrationAuthType,
        connectivityTest: (i.connectivity_test ?? null) as ConnectivityTestOverride | null,
        // ENG-7543: bind-probe this connection's own server (default fleet: 'default').
        connectionKey: (i as { connection_key?: string | null }).connection_key ?? null,
      }),
    })),
    {
      mcpJson,
      missingStdioKeys,
      anyStdioServerLive,
      transportDownKeys: () => readSlackTransportDownKeys(projectDir),
      probeHttp,
      sessionLoadedServerKeys: opts.sessionLoadedServerKeys,
      sessionStartedMs: opts.sessionStartedMs,
      intervalMs: opts.intervalMs,
      ...(opts.maxPerRun !== undefined ? { maxPerRun: opts.maxPerRun } : {}),
    },
  );
}


/**
 * ENG-9048 AC3 — how long Slack inbound may be dead before it counts as a
 * transport failure worth acting on.
 *
 * Well above ordinary churn: one agent logged 84 disconnects in a week, and the
 * healthy reconnect path completes within a second. The incident this exists
 * for ran 3h47m, so the cost of waiting five minutes to be sure is negligible
 * against the cost of restarting an MCP child on a blip.
 */
const SLACK_TRANSPORT_DOWN_AFTER_MS = 300_000;

/** The `.mcp.json` key the Slack channel MCP is declared under. */
const SLACK_MCP_SERVER_KEY = 'slack';

/**
 * Read the Slack channel's published socket state and decide whether its
 * transport counts as down.
 *
 * FAILS SAFE IN ONE DIRECTION ONLY. A missing file, an unreadable file, an
 * unparseable one, a stale one, or a socket that is merely bouncing all return
 * an EMPTY set — never a down verdict. The consumer of `unreachable` SIGTERMs
 * the MCP child and its flag is enabled globally, so the only input allowed to
 * condemn a server is a fresh file that positively says the socket has been
 * down past the threshold. This mirrors how the caller already treats a failed
 * `ps`: bail rather than act on absent evidence.
 */
function readSlackTransportDownKeys(projectDir: string): ReadonlySet<string> {
  const none: ReadonlySet<string> = new Set();
  try {
    // ADR-0049: derived from `projectDir` rather than re-joining '.augmented'
    // with a code name. That is not just guard-appeasement — the per-agent dir
    // is migrating from ~/.augmented/{codeName} to ~/.augmented/{agent_id}, so a
    // fresh codename join would land on the wrong side of the compatibility
    // symlink and this reader would silently stop finding a file the writer was
    // still producing. The agent dir is `projectDir`'s parent, and the caller
    // already resolved `projectDir` through the canonical seam, so reader and
    // writer stay anchored to the same place whatever the layout does next.
    const raw = readFileSync(join(dirname(projectDir), SLACK_SOCKET_STATE_FILENAME), 'utf-8');
    const state = parseSlackSocketState(raw, Date.now());
    return isSlackInboundTransportDown(state, SLACK_TRANSPORT_DOWN_AFTER_MS)
      ? new Set([SLACK_MCP_SERVER_KEY])
      : none;
  } catch {
    // No file yet, no Slack channel on this agent, or an unreadable path. All
    // mean "no verdict", which is the safe answer.
    return none;
  }
}
